import { DOCUMENT } from '@angular/common'
import { Injectable, inject } from '@angular/core'
import { firstValueFrom, Observable } from 'rxjs'
import { SeFeApiService } from 'se-fe-api'
import { SeFeDomainEnvironmentService } from 'se-fe-domain-environment'

import { ContactCreateParams, nginV2Headers } from '@se-po/shared-data-access-models'

export interface ExportSyncParams {
  org_id: number
  group_persona_id?: string | number
  sort?: string
  sort_dir?: string
  sort_source?: string
  search?: string
  filters?: Record<string, unknown>[]
  columns?: string[]
  site_membership_status?: string[]
}

export interface ExportAsyncParams {
  org_id: number
  group_persona_id?: string | number
  search?: string
  filters?: Record<string, unknown>[]
  columns?: string[]
  site_membership_status?: string[]
}

export interface ContactField {
  source?: string
  key: string
  field: string
}

export interface ContactFilter {
  field: string
  logic: string
  source?: string
  value?: unknown
  values?: unknown[]
  or?: ContactFilter[]
}

export interface ContactFilterParams {
  fields?: ContactField[]
  filters?: ContactFilter[]
}

export interface BulkContactParams {
  excludeIds?: Array<string | number>
  fields?: ContactField[]
  filters?: ContactFilter[]
  search?: string
  site_membership_status?: string[]
  [key: string]: unknown
}

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private env = inject(SeFeDomainEnvironmentService)
  private doc = inject(DOCUMENT)

  constructor(private api: SeFeApiService) {
    // noop
  }

  public create(orgId: string, params: ContactCreateParams): Observable<any> {
    params = { org_id: orgId, ...params }
    return this.api.post('contact_list', params, { headers: nginV2Headers })
  }

  public findAll(orgId: string, params: any): Observable<any> {
    params = {
      org_id: orgId,
      exclude_teams: 'true',
      per_page: 1000,
      ...params,
    }
    return this.api.post('contact_list/filter', params, {
      headers: nginV2Headers,
    })
  }

  public async findAllForBulkAction(orgId: string, params: BulkContactParams = {}): Promise<any[]> {
    const perPage = 1000
    const { excludeIds = [], ...requestParams } = params
    const firstPage = await firstValueFrom(this.findAll(orgId, {
      ...requestParams,
      page: 1,
      per_page: perPage
    }))
    const results = [...(firstPage?.result || [])]
    const totalPages = firstPage?.metadata?.pagination?.total_pages || 1

    for (let page = 2; page <= totalPages; page++) {
      const response = await firstValueFrom(this.findAll(orgId, {
        ...requestParams,
        page,
        per_page: perPage
      }))
      results.push(...(response?.result || []))
    }

    return this.excludeContacts(results, excludeIds)
  }

  private excludeContacts(contacts: any[], excludeIds: Array<string | number>): any[] {
    if (!excludeIds.length) return contacts

    const excluded = new Set(excludeIds.map(id => id.toString()))
    return contacts.filter(contact => !excluded.has(contact?.id?.toString()))
  }

  /**
   * Sync export: creates a hidden form and POSTs to export.csv endpoint.
   * Browser opens CSV in a new tab. Used for <= 2000 records.
   */
  public exportSync(params: ExportSyncParams): void {
    const baseUrl = this.buildExportUrl('ngin/v2/contact_list/export.csv')
    const urlParams = this.buildSyncUrlParams(params)
    const actionUrl = urlParams ? `${baseUrl}?${urlParams}` : baseUrl

    const form = this.doc.createElement('form')
    form.method = 'POST'
    form.action = actionUrl
    form.target = '_blank'
    form.style.display = 'none'

    if (params.search) {
      form.appendChild(this.createHiddenInput('search', params.search))
    }

    if (params.filters?.length) {
      params.filters.forEach(filter => {
        form.appendChild(this.createHiddenInput('filters[]', JSON.stringify(filter)))
      })
    }

    if (params.site_membership_status?.length) {
      params.site_membership_status.forEach(status => {
        form.appendChild(this.createHiddenInput('site_membership_status[]', status))
      })
    }

    if (params.columns?.length) {
      form.appendChild(this.createHiddenInput('columns[]', 'se_profile.*.name'))
      params.columns.forEach(col => {
        form.appendChild(this.createHiddenInput('columns[]', col))
      })
    }

    this.doc.body.appendChild(form)
    form.submit()
    form.remove()
  }

  /**
   * Async export: POSTs to export_async endpoint with params in the body.
   * Used for > 2000 records. User receives email with download link.
   */
  public exportAsync(params: ExportAsyncParams): Observable<unknown> {
    const body = new URLSearchParams()
    body.append('org_id', params.org_id.toString())

    if (params.group_persona_id !== undefined) {
      body.append('group_persona_id', params.group_persona_id.toString())
    }

    if (params.search) {
      body.append('search', params.search)
    }

    if (params.filters?.length) {
      params.filters.forEach(filter => {
        body.append('filters[]', JSON.stringify(filter))
      })
    }

    if (params.site_membership_status?.length) {
      params.site_membership_status.forEach(status => {
        body.append('site_membership_status[]', status)
      })
    }

    if (params.columns?.length) {
      body.append('columns[]', 'se_profile.*.name')
      params.columns.forEach(col => {
        body.append('columns[]', col)
      })
    }

    return this.api.post('ngin/v2/contact_list/export_async', body.toString(), {
      headers: {
        ...nginV2Headers,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
  }

  public reindexOrgPersonaIndex(orgId: number): Observable<unknown> {
    return this.api.post('org_persona_index/reindex', { org_id: orgId }, { headers: nginV2Headers })
  }

  public indexSurvey(surveyId: number): Observable<unknown> {
    return this.api.post<void>(`org_persona_index/index_survey?survey_id=${surveyId}`, {}, { headers: nginV2Headers })
  }

  private buildExportUrl(path: string): string {
    const apiUrl = this.env.getUrl('api')
    const base = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`
    return `${base}${path}`
  }

  private buildSyncUrlParams(params: ExportSyncParams): string {
    const parts: string[] = [`org_id=${params.org_id}`]

    if (params.group_persona_id !== undefined) {
      parts.push(`group_persona_id=${encodeURIComponent(params.group_persona_id)}`)
    }

    if (params.sort) {
      parts.push(`sort=${encodeURIComponent(params.sort)}`)
    }
    if (params.sort_dir) {
      parts.push(`sort_dir=${encodeURIComponent(params.sort_dir)}`)
    }
    if (params.sort_source) {
      parts.push(`sort_source=${encodeURIComponent(params.sort_source)}`)
    }

    return parts.join('&')
  }

  private createHiddenInput(name: string, value: string): HTMLInputElement {
    const input = this.doc.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    return input
  }
}
